import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import {
  DisablePaypalIntegrationResponse,
  FetchPaypalOnboardingLinkResponse,
  FetchShorePayOnlineOnboardingLinkResponse,
  SubPaymentMethodNameV2,
  IMerchantPaymentSettingsV2,
  UpdatePaymentMethodsV2Payload,
} from '../types';
import {
  disablePaypalIntegration,
  fetchPaypalOnboardingLink,
  fetchShorePayOnlineOnboardingLink,
  toggleSubPaymentMethodV2,
  updatePaymentMethodsV2,
} from '../api/payments';
import createQueryConfig from '../../queries';
import { publishUIMessage } from '../utils/notifications';
import { t } from '../../utils/i18n';

export const paymentErrors = {
  vatIdOrTaxNumberRequired: 'VAT ID or Tax Number is required',
};

export function useDisablePayPalIntegration() {
  const { apiClient } = useGlobals();
  return useMutation({
    mutationKey: ['disable-paypal-integration'],
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      disablePaypalIntegration({ apiClient, merchantId }),
    onSuccess: (_data: DisablePaypalIntegrationResponse) => {
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        console.error('Error disabling PayPal integration:');
        publishUIMessage('error', error.message);
      }
    },
  });
}

export function useInitiatePayPalSetup() {
  const { apiClient } = useGlobals();
  return useMutation({
    mutationKey: ['initiate-paypal-setup'],
    mutationFn: ({ merchantId }: { merchantId: string }) =>
      fetchPaypalOnboardingLink({ apiClient, merchantId }),
    onSuccess: (data: FetchPaypalOnboardingLinkResponse) => {
      const paypalSetupURL = data?.links?.action_url;
      if (paypalSetupURL) {
        setTimeout(() => {
          window.open(paypalSetupURL, '_blank')?.focus();
        }, 0);
      }
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        console.error('Error fetching PayPal signup link:');
        publishUIMessage('error', error.message);
      }
    },
  });
}

export function useInitiateShorePayOnlineAccountSetup() {
  const { apiClient } = useGlobals();
  return useMutation({
    mutationKey: ['initiate-shore-pay-online-setup'],
    mutationFn: ({
      taxId,
      merchantId,
      company,
    }: {
      taxId?: string;
      merchantId: string;
      company: string;
    }) =>
      fetchShorePayOnlineOnboardingLink({
        apiClient,
        company,
        taxId,
        merchantId,
      }),
    onSuccess: (result: FetchShorePayOnlineOnboardingLinkResponse) => {
      const { data: shorePayOnlineData, errors: shorePayOnlineErrors } = result;
      if (shorePayOnlineErrors && shorePayOnlineErrors.length > 0) {
        const [error] = shorePayOnlineErrors;
        const { title } = error || {};
        if (title) {
          throw new Error(title);
        }
      } else if (shorePayOnlineData) {
        const { attributes: { url: shorePaySetupURL } = {} } =
          shorePayOnlineData || {};

        if (shorePaySetupURL) {
          // Safari doesn't support window.open() inside async calls for security reasons
          // therefore, using wrapping it with a setTimeout to run it outside the async call
          setTimeout(() => {
            window.open(shorePaySetupURL, '_blank')?.focus();
          }, 0);
          return;
        }
      }

      throw new Error('ShorePay account setup error');
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        if (!error.message.includes(paymentErrors.vatIdOrTaxNumberRequired)) {
          publishUIMessage(
            'error',
            t('notifications.general.customer-support') || ''
          );
        }
        console.error('ShorePay account setup error');
      }
    },
  });
}

export function useFetchSubPaymentMethodsV2(
  merchantId: string,
  enabled: boolean
) {
  const { queryClient, apiClient } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);

  const { data, isLoading, isFetching } = useQuery<IMerchantPaymentSettingsV2>({
    ...queryConfig.merchantPaymentSettingsV2b({ merchantId }),
    enabled: enabled && Boolean(merchantId),
  });

  const shorePayOnline = data?.payment_methods?.find(
    (m) => m.name === 'shorepay_online'
  );

  return {
    data: shorePayOnline?.meta?.payment_methods ?? [],
    isLoading,
    isFetching,
  };
}

export function useUpdatePaymentMethodsV2() {
  const { apiClient } = useGlobals();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['update-payment-methods-v2'],
    mutationFn: (variables: {
      merchantId: string;
      payload: UpdatePaymentMethodsV2Payload;
    }) => updatePaymentMethodsV2({ apiClient, payload: variables.payload }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['merchant_payment_settings_v2b', variables.merchantId],
      });
      publishUIMessage(
        'success',
        t('notifications.booking-configurations.success-message')
      );
    },
    onError: () => {
      publishUIMessage(
        'error',
        t('notifications.booking-configurations.error-message')
      );
    },
  });
}

export function useToggleSubPaymentMethodV2() {
  const { apiClient } = useGlobals();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggle-sub-payment-method-v2'],
    mutationFn: ({
      merchantId,
      subPaymentMethodName,
      enabled,
    }: {
      merchantId: string;
      subPaymentMethodName: SubPaymentMethodNameV2;
      enabled: boolean;
    }) =>
      toggleSubPaymentMethodV2({
        apiClient,
        merchantId,
        subPaymentMethodName,
        enabled,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['merchant_payment_settings_v2b', variables.merchantId],
      });
    },
    onError: (error: unknown) => {
      if (error instanceof Error) {
        publishUIMessage('error', error.message);
      }
    },
  });
}
